import { isNonNullableObject, } from "@knyt/artisan";
/**
 * Determines whether the input is a {@link LifecycleDelegateHost}.
 *
 * @public
 */
export function isLifecycleDelegateHost(value) {
    return (isNonNullableObject(value) &&
        "addDelegate" in value &&
        typeof value.addDelegate === "function" &&
        "removeDelegate" in value &&
        typeof value.removeDelegate === "function");
}
/**
 * A basic implementation of the {@link LifecycleDelegateHost} interface.
 *
 * @internal scope: package
 */
export class BasicLifecycleDelegateHost {
    /**
     * A set of resource renderers that are added to the host.
     *
     * @remarks
     *
     * This must be a `Set` to ensure that the same renderer
     * is not added multiple times, which could lead to
     * unexpected behavior when rendering.
     */
    #hookRegistry = new Set();
    /**
     * Registers a lifecycle delegate to the host instance.
     */
    addDelegate(hooks) {
        this.#hookRegistry.add(hooks);
    }
    /**
     * Removes a previously registered lifecycle delegate from the host instance.
     */
    removeDelegate(hooks) {
        this.#hookRegistry.delete(hooks);
    }
    /**
     * Removes all registered lifecycle delegates from the host.
     *
     * @remarks
     *
     * This is useful for cleaning up all delegates at once,
     * for example when the host is being destroyed or reset.
     *
     * This method is used during hot module replacement.
     *
     * @internal scope: workspace
     */
    clearDelegates() {
        this.#hookRegistry.clear();
    }
    addLifecycleHook(hookName, hook) {
        const hooks = {
            [hookName]: hook,
        };
        this.addDelegate(hooks);
        return {
            unsubscribe: () => {
                this.removeDelegate(hooks);
            },
        };
    }
    /**
     * Checks whether the specified lifecycle hook
     * should be invoked based on the registered hooks.
     *
     * @remarks
     *
     * If there are no hooks, we can return immediately.
     * Don't return a promise if we can avoid it,
     * as it can lead to unnecessary microtask scheduling.
     */
    #shouldInvokeHooks(name) {
        if (this.#hookRegistry.size === 0) {
            // If there are no hooks, we can return false immediately.
            return false;
        }
        // Check if any of the hooks have the specified method
        // to determine whether we should invoke the hooks.
        return Array.from(this.#hookRegistry).some((hooks) => !!hooks[name]);
    }
    /**
     * Invokes the provided callback for each registered lifecycle hooks.
     */
    async #invokeHooks(callback) {
        // All side effects should be asynchronous.
        // Wait until the next microtask to ensure that
        // all side effects are async.
        await Promise.resolve();
        // All hooks are called in parallel, so we can use
        // add them to an array and then use `Promise.all`
        // to wait for all of them to complete.
        //
        // NOTE: The use of `Array.from` serves two purposes:
        // 1. It creates a shallow copy of the set, so that
        //    we can iterate over it without worrying about
        //    the set being modified while we iterate.
        // 2. It converts the set to an array, so that we can
        //    use `map` to create an array of promises
        //    that we can wait for.
        await Promise.all(Array.from(this.#hookRegistry).map((hooks) => callback(hooks)));
    }
    #handleInvokeError(error, abortController) {
        // If an error occurs anywhere in the lifecycle hooks,
        // we want to abort the operation using the provided
        // AbortController, if it hasn't already been aborted.
        if (abortController?.signal.aborted === false) {
            abortController.abort(error);
        }
        // Re-throw the error here, as we want to propagate it
        // to the caller of `performUpdateRequested`.
        throw error;
    }
    async #invokeUpdateRequested(payload) {
        try {
            await this.#invokeHooks((hooks) => hooks.hostUpdateRequested?.(payload));
        }
        catch (error) {
            this.#handleInvokeError(error, payload.abortController);
        }
    }
    async #invokeInterrupted(interrupt) {
        try {
            await this.#invokeHooks((hooks) => hooks.hostInterrupted?.(interrupt));
        }
        catch (error) {
            this.#handleInvokeError(error);
        }
    }
    async #invokeBeforeMount(payload) {
        try {
            await this.#invokeHooks((hooks) => hooks.hostBeforeMount?.(payload));
        }
        catch (error) {
            this.#handleInvokeError(error, payload.abortController);
        }
    }
    async #invokeBeforeUpdate(payload) {
        try {
            await this.#invokeHooks((hooks) => hooks.hostBeforeUpdate?.(payload));
        }
        catch (error) {
            this.#handleInvokeError(error, payload.abortController);
        }
    }
    async #invokeAfterUpdate(payload) {
        try {
            await this.#invokeHooks((hooks) => hooks.hostAfterUpdate?.(payload));
        }
        catch (error) {
            this.#handleInvokeError(error);
        }
    }
    async #invokeHookWithoutPayload(hookName) {
        try {
            await this.#invokeHooks((hooks) => hooks[hookName]?.());
        }
        catch (error) {
            this.#handleInvokeError(error);
        }
    }
    /**
     * Performs the `hostBeforeMount` lifecycle method for all hooks.
     *
     * @remarks
     *
     * This method is called by the host when the element is connected to the DOM,
     * before the element is rendered.
     * It calls the `hostBeforeMount` method of all hooks in parallel,
     * and waits for all of them to complete before returning.
     *
     * If any hook throws an error, the operation is aborted and the error
     * is re-thrown to propagate to the caller.
     */
    /*
     * ### Private Remarks
     *
     * tldr; a bit of micro-optimization doesn't hurt.
     *
     * This method is intentionally not marked as `async`,
     * because it may only perform synchronous operations
     * and we want to avoid the overhead of creating a
     * microtask for the promise resolution;
     */
    performBeforeMount(payload) {
        if (this.#shouldInvokeHooks("hostBeforeMount")) {
            return this.#invokeBeforeMount(payload);
        }
    }
    /**
     * Performs the `hostUpdateRequested` lifecycle method for all hooks.
     *
     * @remarks
     *
     * This method is called by the host before the element is updated.
     * It calls the `hostUpdateRequested` method of all hooks in parallel,
     * and waits for all of them to complete before returning.
     *
     * If any hook throws an error, the operation is aborted and the error
     * is re-thrown to propagate to the caller.
     */
    /*
     * ### Private Remarks
     *
     * tldr; a bit of micro-optimization doesn't hurt.
     *
     * This method is intentionally not marked as `async`,
     * because it may only perform synchronous operations
     * and we want to avoid the overhead of creating a
     * microtask for the promise resolution;
     */
    performUpdateRequested(payload) {
        if (this.#shouldInvokeHooks("hostUpdateRequested")) {
            return this.#invokeUpdateRequested(payload);
        }
    }
    /**
     * Performs the `hostInterrupted` lifecycle method for all hooks.
     *
     * @remarks
     *
     * This method is called by the host when the element's lifecycle is
     * interrupted. It calls the `hostInterrupted` method of all hooks in
     * parallel, and waits for all of them to complete before returning.
     *
     * If any hook throws an error, the error is re-thrown to propagate
     * to the caller.
     */
    /*
     * ### Private Remarks
     *
     * tldr; a bit of micro-optimization doesn't hurt.
     *
     * This method is intentionally not marked as `async`,
     * because it may only perform synchronous operations
     * and we want to avoid the overhead of creating a
     * microtask for the promise resolution;
     */
    performInterrupted(interrupt) {
        if (this.#shouldInvokeHooks("hostInterrupted")) {
            return this.#invokeInterrupted(interrupt);
        }
    }
    performMounted() {
        // This is intentionally not awaited: post-event hooks are not async/cancelable.
        // If async, they run in parallel.
        this.#invokeHookWithoutPayload("hostMounted");
    }
    performBeforeUpdate(payload) {
        // This is intentionally not awaited: post-event hooks are not async/cancelable.
        // If async, they run in parallel.
        this.#invokeBeforeUpdate(payload);
    }
    performAfterUpdate(payload) {
        // These are intentionally not awaited: post-event hooks are not async/cancelable.
        // If async, they run in parallel.
        this.#invokeAfterUpdate(payload);
    }
    performUnmounted() {
        // This is intentionally not awaited: post-event hooks are not async/cancelable.
        // If async, they run in parallel.
        this.#invokeHookWithoutPayload("hostUnmounted");
    }
    /**
     * Synchronously handles an error that occurs during a lifecycle event.
     * Each error handler registered via `onError` will be called in turn
     * with the error that occurred.
     *
     * @returns `true` if the error was handled by at least one error handler
     *          `false` if the error was not handled by any error handler.
     */
    handleError(error) {
        // If there are no hooks, we can return immediately.
        if (this.#hookRegistry.size === 0)
            return false;
        // Create a shallow array copy of the hooks set
        // to safely iterate and call each hook's `hostErrorCaptured` method,
        // even if the set is modified during iteration.
        const hooksInstancesWithErrorHandler = Array.from(this.#hookRegistry).filter((hooks) => hooks.hostErrorCaptured);
        if (hooksInstancesWithErrorHandler.length === 0) {
            // If no hooks have an error handler, we can return false.
            return false;
        }
        try {
            for (const hooks of hooksInstancesWithErrorHandler) {
                hooks.hostErrorCaptured?.(error);
            }
        }
        catch (error) {
            console.error("An error occurred while handling the error in lifecycle hooks:", error);
        }
        return true;
    }
}
/**
 * @internal scope: workspace
 */
export class LifecycleAdapter extends BasicLifecycleDelegateHost {
    /**
     * Registers a lifecycle hook to be called before the host is mounted.
     *
     * @param hostBeforeMount - The callback to invoke before mounting.
     * @returns A subscription object that can be used to remove the hook.
     */
    onBeforeMount(hostBeforeMount) {
        return this.addLifecycleHook("hostBeforeMount", hostBeforeMount);
    }
    /**
     * Registers a lifecycle hook to be called after the host is mounted to the DOM.
     *
     * @param hostMounted - The callback to invoke when the host is mounted.
     * @returns A subscription object that can be used to remove the hook.
     */
    onMounted(hostMounted) {
        return this.addLifecycleHook("hostMounted", hostMounted);
    }
    /**
     * Registers a lifecycle hook to be called when an update is requested on the host.
     *
     * @param hostUpdateRequested - The callback to invoke before updating.
     * @returns A subscription object that can be used to remove the hook.
     */
    onUpdateRequested(hostUpdateRequested) {
        return this.addLifecycleHook("hostUpdateRequested", hostUpdateRequested);
    }
    /**
     * Registers a lifecycle hook to be called before an update is performed on the host.
     *
     * @param hostBeforeUpdate - The callback to invoke when an update is performed.
     * @returns A subscription object that can be used to remove the hook.
     */
    onBeforeUpdate(hostBeforeUpdate) {
        return this.addLifecycleHook("hostBeforeUpdate", hostBeforeUpdate);
    }
    /**
     * Registers a lifecycle hook to be called after the host has been updated.
     *
     * @param hostAfterUpdate - The callback to invoke after the host update is complete.
     * @returns A subscription object that can be used to remove the hook.
     */
    onAfterUpdate(hostAfterUpdate) {
        return this.addLifecycleHook("hostAfterUpdate", hostAfterUpdate);
    }
    /**
     * Registers a lifecycle hook to be called when the host's lifecycle is interrupted.
     *
     * @param hostInterrupted - The callback to invoke when the host's lifecycle is interrupted.
     * @returns A subscription object that can be used to remove the hook.
     */
    onInterrupted(hostInterrupted) {
        return this.addLifecycleHook("hostInterrupted", hostInterrupted);
    }
    /**
     * Registers a lifecycle hook to be called when the host is unmounted from the DOM.
     *
     * @param hostUnmounted - The callback to invoke when the host is unmounted.
     * @returns A subscription object that can be used to remove the hook.
     */
    onUnmounted(hostUnmounted) {
        return this.addLifecycleHook("hostUnmounted", hostUnmounted);
    }
    /**
     * Registers a lifecycle hook to be called when an error is captured during any lifecycle event.
     *
     * @param hostErrorCaptured - The error handler callback to invoke when an error occurs in a lifecycle hook.
     * @returns A subscription object that can be used to remove the error handler.
     */
    onErrorCaptured(hostErrorCaptured) {
        return this.addLifecycleHook("hostErrorCaptured", hostErrorCaptured);
    }
}
